import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../pages/auth/AuthContext';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Star } from 'lucide-react';

const ClientReviewTherapist = () => {
  const { therapistId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useContext(AuthContext);
  const [rating, setRating] = useState(0);
  const [reviewContent, setReviewContent] = useState('');
  const [therapist, setTherapist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accessToken) {
      toast.error("You must be logged in to leave a review.");
      navigate('/login');
      return;
    }

    const fetchTherapistDetails = async () => {
      try {
        const response = await fetch(`https://quirkleai.com/api/therapists/${therapistId}/`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch therapist details');
        }
        const data = await response.json();
        setTherapist(data);
      } catch (err) {
        console.error("Error fetching therapist details:", err);
        setError("Failed to load therapist details.");
        toast.error("Failed to load therapist details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTherapistDetails();
  }, [therapistId, accessToken, navigate]);

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error("Please select a rating.");
      return;
    }
    if (!reviewContent.trim()) {
      toast.error("Please write your review.");
      return;
    }

    try {
      const response = await fetch('https://quirkleai.com/api/testimonials/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          therapist: therapistId,
          rating: rating,
          content: reviewContent,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit review');
      }

      toast.success('Review submitted successfully!');
      navigate('/client/bookings'); // Or wherever appropriate after review
    } catch (err) {
      console.error("Error submitting review:", err);
      toast.error(`Error submitting review: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading therapist details...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">Error: {error}</div>;
  }

  if (!therapist) {
    return <div className="text-center py-10">Therapist not found.</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Leave a Review for {therapist.username}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Your Rating:</h3>
            <div className="flex space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`cursor-pointer ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                  onClick={() => setRating(i + 1)}
                  size={30}
                />
              ))}
            </div>
          </div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Your Review:</h3>
            <Textarea
              placeholder="Share your experience..."
              value={reviewContent}
              onChange={(e) => setReviewContent(e.target.value)}
              rows={6}
            />
          </div>
          <Button onClick={handleSubmitReview} className="w-full">Submit Review</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientReviewTherapist;
